import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { client, retryTransient } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { createAssistantActions } from "./assistantActions";
import { reconcileAssistantView, sortAssistantConversations, useAssistantDrawer } from "./assistantStore";

export const DRAFT_CHAT_ID = "";

export function useAssistant(orgId: string, context: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const generation = useAssistantDrawer((state) => state.generation);
  // Account scope is part of the cache key as well as the presentation state.
  // Org-level invalidations (including settings) still match this prefix.
  const queryKey = useMemo(() => [...qk.assistant(orgId), context], [orgId, context]);
  const stateQuery = useQuery({ queryKey, queryFn: () => client.assistantState(orgId), retry: retryTransient, enabled });
  const view = useAssistantDrawer((state) => state.view);
  const busy = useAssistantDrawer((state) => state.busy);
  const error = useAssistantDrawer((state) => state.error);
  const conversations = useMemo(() => sortAssistantConversations(stateQuery.data?.conversations ?? []), [stateQuery.data]);

  useEffect(() => {
    if (!stateQuery.data) return;
    const next = reconcileAssistantView(view, conversations);
    if (next !== view) useAssistantDrawer.getState().showList();
  }, [conversations, stateQuery.data, view]);

  const actions = useMemo(() => createAssistantActions({
    orgId, context, generation, queryKey, queryClient, client,
  }), [orgId, context, generation, queryKey, queryClient]);

  return {
    ...actions, conversations, busy, error,
    available: stateQuery.data?.available ?? false,
    unavailableReason: stateQuery.data?.unavailableReason,
    isPending: stateQuery.isPending,
    isError: stateQuery.isError,
    starting: busy === "start",
    refetch: () => void stateQuery.refetch(),
    dismissError: () => useAssistantDrawer.setState({ error: undefined }),
  };
}
